/* eslint-disable indent */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import AudioPlayer from 'react-h5-audio-player';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Modal, MultiSelect } from '@/common';
import { UploadButton, UploadButtonRef } from '@/components/common';
import { Search } from '@/components/common/Search';
import { IconButton, Label, LABEL_TYPE, Spinner } from '@/components/generic';
import { IconButtonVariant } from '@/components/generic/IconButton/icon-button-config';
import { queryClient } from '@/configuration/react-query';
import {
  AUDIO,
  MIN_SONG_DURATION,
  PAGE_SIZE,
  STORAGE_LIMIT_EXCEEDED_ERROR_CODE,
  TIME_LIMIT_EXCEEDED_ERROR_CODE,
  TRIBUTE_VIDEO_MUSIC,
  UPLOAD_PICKER_TYPES
} from '@/constants';
import { AuthContext, OfflineContext } from '@/contexts';
import { AssetScope, AssetType, COLORS, PERMISSION_TYPE } from '@/enums';
import { ORGANIZATION_PERMISSION_TYPE } from '@/enums/OrganizationPermissionEnum';
import { groupBy } from '@/helpers';
import { showToast, ToastType } from '@/helpers/toast';
import { useFileUpload, useMobileScreen } from '@/hooks';
import { AssetTagItem } from '@/pages/Event/Asset/AssetTagItem';
import { ChosenSongsContainer } from '@/pages/Event/components/ChosenSongsContainer';
import {
  DeleteIcon,
  MusicIcon,
  MusicNoteIcon,
  NoMusicIcon,
  PauseIcon,
  PlayIcon,
  PlusIcon
} from '@/resources';
import {
  useDeleteAssetMutation,
  useGetEventAssets,
  useGetEventByIdQuery,
  useGetEventTags
} from '@/services';
import {
  useDownloadSongMutation,
  usePreviewMusicQuery,
  useSearchMusicQuery
} from '@/services/music.service';
import { getTestId, TestIds } from '@/shared/test-ids';
import { Asset, AssetSlot, DownloadSong, SelectOptionProps, Tag, TributeVideoAsset } from '@/types';

type MusicProps = {
  isTab?: boolean;
  setEnableNext?: React.Dispatch<React.SetStateAction<boolean>>;
  selectedSongs?: TributeVideoAsset[];
  setSelectedSongs?: React.Dispatch<React.SetStateAction<TributeVideoAsset[]>>;
  isCreateVideo?: boolean;
  hasEvent?: boolean;
  setPlaylistAssets?: React.Dispatch<React.SetStateAction<Asset[]>>;
  setAllowReviewStep?: React.Dispatch<React.SetStateAction<boolean>>;
  playlistAssets?: Asset[];
  organizationId?: number;
  playlistId?: number;
  organizationPermissions?: string[];
};

export const Music = ({
  isTab = false,
  setEnableNext,
  selectedSongs,
  setSelectedSongs,
  isCreateVideo = false,
  hasEvent = true,
  setPlaylistAssets,
  setAllowReviewStep,
  playlistAssets,
  organizationId,
  playlistId,
  organizationPermissions: eventOrganizationPermissions
}: MusicProps) => {
  const [t] = useTranslation('common');
  const { eventId } = useParams();
  const [songUrl, setSongUrl] = useState<string | undefined>('');
  const [selectedPublicSong, setSelectedPublicSong] = useState<DownloadSong>();
  const [selectedSong, setSelectedSong] = useState<Asset>();
  const [selectedSongsTab, setSelectedSongsTab] = useState<Asset[]>([]);
  const [selectedSongTags, setSelectedSongTags] = useState<SelectOptionProps[]>([]);
  const isMobile = useMobileScreen();
  const [search, setSearch] = useState<string>('');
  const [previewSongId, setPreviewSongId] = useState<number | undefined>();
  const [previewSongName, setPreviewSongName] = useState<string | undefined>();
  const [showMinLengthMessage, setShowMinLengthMessage] = useState<boolean>(false);
  const uploadButtonRef = useRef<UploadButtonRef>(null);
  const pendingDeletedSongIdRef = useRef<number | null>(null);

  const {
    currentUserData: currentUser,
    permissions,
    organizationPermissions: userOrganizationPermissions
  } = useContext(AuthContext);
  const organizationPermissions = eventOrganizationPermissions ?? userOrganizationPermissions;

  const {
    isOnline,
    getOfflineSongsForEvent,
    getOfflinePlaylist,
    getAudioBlobUrl,
    removeOfflineSong
  } = useContext(OfflineContext);

  const [offlineSongUrl, setOfflineSongUrl] = useState<string | null>(null);
  const offlinePlaylistPlayerRef = useRef<AudioPlayer>(null);
  const [shouldAutoPlayOfflineNext, setShouldAutoPlayOfflineNext] = useState(false);
  const offlineSongs = isTab && hasEvent ? getOfflineSongsForEvent(Number(eventId)) : [];

  const [offlineEventBlobUrls, setOfflineEventBlobUrls] = useState<Record<number, string>>({});

  useEffect(() => {
    if (!isOnline && isTab && hasEvent && offlineSongs.length > 0) {
      (async () => {
        const urls: Record<number, string> = {};
        for (const song of offlineSongs) {
          const url = await getAudioBlobUrl(song.id);
          if (url) {
            urls[song.id] = url;
          }
        }
        setOfflineEventBlobUrls(urls);
      })();
    }
  }, [isOnline, isTab, hasEvent, offlineSongs.length]);

  const offlinePlaylistData = !hasEvent && playlistId ? getOfflinePlaylist(playlistId) : undefined;

  const [currentOfflineSongId, setCurrentOfflineSongId] = useState<number | null>(null);
  const { isLoading: isLoadingTags, data: allTags } = useGetEventTags(eventId ?? '');

  const { data: eventData, isLoading: isLoadingEvent } = useGetEventByIdQuery(
    Number(eventId) ?? ''
  );

  const hasOrgAccess = permissions.includes(PERMISSION_TYPE.ORG_ACCESS);

  const hasMusicProviderAccess = organizationPermissions.includes(
    ORGANIZATION_PERMISSION_TYPE.MUSIC_PROVIDER
  );
  const hasContentManagementAccess = organizationPermissions.includes(
    ORGANIZATION_PERMISSION_TYPE.CONTENT_MANAGEMENT
  );
  const clickRef = useRef(false);

  const tagIds: string[] =
    allTags?.data?.length > 0
      ? allTags?.data.filter((tag: Tag) => tag.name === AUDIO).map((tag: Tag) => `${tag.id}`)
      : [];

  const {
    data: allAssets,
    refetch,
    isLoading: isLoadingAssets
  } = useGetEventAssets(
    String(eventId),
    hasOrgAccess ? [] : tagIds,
    PAGE_SIZE,
    '',
    [AssetType.AUDIO.toString()],
    AssetScope.PRIVATE,
    undefined,
    eventId ? hasContentManagementAccess : false
  );

  const { mutate: deleteAsset } = useDeleteAssetMutation(queryClient, {
    onSuccess: () => {
      const deletedSongId = pendingDeletedSongIdRef.current;
      if (deletedSongId && eventId) {
        void removeOfflineSong(deletedSongId, Number(eventId));
      }
      pendingDeletedSongIdRef.current = null;
      showToast(ToastType.SUCCESS, t('assetSuccessfullyDeleted'));
    },
    onError: () => {
      pendingDeletedSongIdRef.current = null;
      showToast(ToastType.ERROR, t('assetDeleteError'));
    }
  });

  const { mutate: downloadSong, isLoading: isLoadingDownload } = useDownloadSongMutation(
    queryClient,
    {
      onSuccess: (response) => {
        if (response?.data && (selectedSongs || playlistAssets)) {
          if (hasEvent && selectedSongs) {
            setSelectedSongs?.([
              ...selectedSongs,
              { id: 0, tributeVideoId: 0, asset: response.data, order: 0, durationMultiplier: 1 }
            ]);
          } else {
            setPlaylistAssets?.((prev) => [...(prev ?? []), response.data]);
          }
        }

        showToast(ToastType.SUCCESS, t('downloadSongSuccess'));
        if (hasContentManagementAccess) {
          eventId && refetch();
        }
      },
      onError: (error) => {
        const backendError = error.response?.data?.ErrorMessages?.[0];
        const message = backendError?.DisplayMessage ?? t('downloadSongError');
        const errorCode = backendError?.Code;
        const isLimitExceeded =
          !!errorCode &&
          [STORAGE_LIMIT_EXCEEDED_ERROR_CODE, TIME_LIMIT_EXCEEDED_ERROR_CODE].includes(errorCode);

        if (isLimitExceeded) {
          showToast(ToastType.WARNING, {
            text: message,
            autoClose: false
          });
          return;
        }

        showToast(ToastType.ERROR, message);
      }
    }
  );

  const handleUploadedAudioAsset = (asset: Asset) => {
    if (isTab) {
      if (hasEvent) {
        setSelectedSongsTab((prev) => [...prev, asset]);
      } else {
        setPlaylistAssets?.((prev) => [...(prev ?? []), asset]);
      }

      return;
    }

    if (hasEvent) {
      setSelectedSongs?.((prevSelectedSongs) => [
        ...prevSelectedSongs,
        {
          id: 0,
          tributeVideoId: 0,
          asset,
          order: prevSelectedSongs.length + 1,
          durationMultiplier: 1
        }
      ]);
    }
  };

  const { uploadFiles: uploadAudioFiles, isUploading: isUploadingAudio } = useFileUpload({
    eventId: hasEvent ? eventId : undefined,
    organizationId,
    pickerTypes: UPLOAD_PICKER_TYPES.AUDIO,
    isPublic: false,
    assetScope: hasEvent ? AssetScope.PRIVATE : AssetScope.ORGANIZATION,
    assetTag: hasOrgAccess ? undefined : AUDIO,
    onCreatedAsset: handleUploadedAudioAsset,
    onRefetchNeeded: () => {
      if (eventId) {
        refetch();
      }
    }
  });

  const assets = useMemo(() => {
    return allAssets?.pages.flatMap((page) => page.data?.items) || [];
  }, [allAssets]);

  const songTagOptions: SelectOptionProps[] = useMemo(() => {
    const tagNames = new Set<string>(
      assets.flatMap((asset: Asset) => asset?.tags?.map((tag) => tag.name) ?? [])
    );
    return [...tagNames].map((name) => ({ value: name, label: t(name) }));
  }, [assets, t]);

  const filteredAssets = useMemo(() => {
    if (selectedSongTags.length === 0) {
      return assets;
    }
    const selectedNames = selectedSongTags.map((tag) => tag.value);
    return assets.filter((asset: Asset) =>
      asset?.tags?.some((tag) => selectedNames.includes(tag.name))
    );
  }, [assets, selectedSongTags]);

  const handleSongTagRemove = (tag: SelectOptionProps) => {
    setSelectedSongTags((prev) => prev.filter((selected) => selected.value !== tag.value));
  };

  const assetsAudio = useMemo(() => {
    return assets?.filter(
      (asset: Asset) => asset && hasEvent && asset.tags?.length > 0 && asset.tags[0]?.name === AUDIO
    );
  }, [assets]);

  useEffect(() => {
    if (allAssets) {
      setSelectedSongsTab(allAssets?.pages.flatMap((page) => page.data?.items));
    }
  }, [allAssets]);

  useEffect(() => {
    if (selectedSongs && selectedSongs.length > 0) {
      setEnableNext?.(true);
    } else {
      setEnableNext?.(false);
    }
  }, [selectedSongs]);

  const handleAddSelectedSong = (song?: Asset) => {
    const chosenSong = song ? song : selectedSong;
    if (chosenSong) {
      if (isTab) {
        if (hasEvent) {
          setSelectedSongsTab([...selectedSongsTab, chosenSong]);
          setSelectedSong(undefined);
        } else {
          setPlaylistAssets?.((prev) => [...(prev ?? []), chosenSong]);
        }
      } else {
        const audio = new Audio(chosenSong.assetUrl?.toString());

        audio.addEventListener('loadedmetadata', () => {
          if (audio.duration < MIN_SONG_DURATION) {
            showToast(ToastType.ERROR, t('audioTooShort'));
            return;
          }
          hasEvent
            ? selectedSongs &&
              setSelectedSongs?.([
                ...selectedSongs,
                { id: 0, tributeVideoId: 0, asset: chosenSong, order: 0, durationMultiplier: 1 }
              ])
            : setPlaylistAssets?.((prev) => [...(prev ?? []), chosenSong]);
        });
        setSelectedSong(undefined);

        return () => audio.removeEventListener;
      }
    }
  };

  const searchMusicQuery = useSearchMusicQuery(String(search), hasMusicProviderAccess);
  const music = hasMusicProviderAccess ? searchMusicQuery.data : null;
  const isLoading = hasMusicProviderAccess ? searchMusicQuery.isLoading : false;

  const { data: previewMusic, refetch: refetchPreview } = usePreviewMusicQuery(
    Number(previewSongId)
  );

  useEffect(() => {
    if (previewSongId !== undefined && !isNaN(previewSongId)) {
      refetchPreview();
    }
  }, [previewSongId]);

  useEffect(() => {
    if (previewMusic?.data) {
      setSongUrl(previewMusic.data);
    }
  }, [previewMusic]);

  const handleSelectSong = (song: DownloadSong) => {
    if (!song) {
      setSelectedPublicSong(undefined);
      setSongUrl(undefined);
      return;
    }
    setSelectedPublicSong(song);
    const payload: DownloadSong = {
      id: song.id,
      title: song.title,
      artist: song.artist,
      duration: song.duration,
      tag: hasEvent && !hasOrgAccess ? (isCreateVideo ? TRIBUTE_VIDEO_MUSIC : AUDIO) : undefined,
      eventId: Number(eventId) ?? null,
      organizationId: organizationId ?? eventData?.data?.organization?.id ?? null
    };
    downloadSong({ payload: payload });
  };

  const deleteChosenSong = (id: number, index: number) => {
    if ((isTab && selectedSongsTab) || (!hasOrgAccess && !selectedSongs)) {
      if (hasEvent) {
        setSelectedSongsTab(selectedSongsTab.filter((_, i) => i !== index));
        pendingDeletedSongIdRef.current = id;
        deleteAsset(id);
      } else {
        setPlaylistAssets?.((prev) => {
          if (!prev) {
            return prev;
          }
          const filtered = prev.filter((playlistAsset) => playlistAsset.id !== id);
          return filtered.map((playlistAsset, idx) => ({ ...playlistAsset, order: idx }));
        });
      }
    } else if (selectedSongs && hasEvent) {
      setSelectedSongs?.(selectedSongs.filter((_, i) => i !== index));
    }
  };

  const groupedAssetSlots = groupBy(
    eventData?.data?.assetSlots,
    ({ slotType }: AssetSlot) => slotType?.assetType
  );
  const slotsLength = groupedAssetSlots.get(AUDIO)?.length ?? 0;

  const handleDisable = () => {
    const selectedSongsLength = selectedSongs?.length ?? 0;
    const assetsAudioLength = assetsAudio?.length ?? 0;
    const value = isCreateVideo ? selectedSongsLength : assetsAudioLength;
    return value >= slotsLength;
  };

  const searchCallback = (query: string) => {
    setSearch(String(query));
  };

  const changePreviewData = (assetName: string) => {
    setPreviewSongId?.(undefined);
    setPreviewSongName?.(assetName);
  };

  const handleClick = (song: DownloadSong | Asset, tunify?: boolean) => {
    if (clickRef.current) {
      return;
    }

    clickRef.current = true;

    if (tunify) {
      handleSelectSong(song as DownloadSong);
    } else {
      handleAddSelectedSong(song as Asset);
    }

    setAllowReviewStep?.(true);

    setTimeout(() => {
      clickRef.current = false;
    }, 500);
  };

  useEffect(() => {
    if (search.length > 0 && search.length < 2) {
      const timer = setTimeout(() => {
        setShowMinLengthMessage(true);
      }, 2000);

      return () => {
        clearTimeout(timer);
        setShowMinLengthMessage(false);
      };
    }
  }, [search]);

  const offlineEventAssets = useMemo<Asset[]>(
    () =>
      offlineSongs.map(
        (song) =>
          ({
            id: song.id,
            assetName: song.assetName,
            assetUrl: offlineEventBlobUrls[song.id] ?? '',
            duration: song.duration,
            assetType: AssetType.AUDIO as unknown as string,
            assetScope: AssetScope.PRIVATE,
            thumbnailUrl: '',
            assetSize: 0,
            createdAt: new Date(0),
            updatedAt: new Date(0),
            additionalData: '',
            tags: [],
            tributeVideos: []
          }) as unknown as Asset
      ),
    [offlineSongs, offlineEventBlobUrls]
  );

  const offlinePlaylistAssets = useMemo<Asset[]>(
    () =>
      offlinePlaylistData?.songs.map(
        (song) =>
          ({
            id: song.id,
            assetName: song.assetName,
            assetUrl: `offline-${song.id}`,
            duration: song.duration,
            assetType: AssetType.AUDIO as unknown as string,
            assetScope: AssetScope.PRIVATE,
            thumbnailUrl: '',
            assetSize: 0,
            createdAt: new Date(0),
            updatedAt: new Date(0),
            additionalData: '',
            tags: [],
            tributeVideos: []
          }) as unknown as Asset
      ) ?? [],
    [offlinePlaylistData]
  );

  const setOfflineSongUrlBridge = useCallback(
    (valueOrUpdater: React.SetStateAction<string | undefined>) => {
      const newValue =
        typeof valueOrUpdater === 'function'
          ? valueOrUpdater(currentOfflineSongId ? `offline-${currentOfflineSongId}` : undefined)
          : valueOrUpdater;

      if (newValue?.startsWith('offline-')) {
        const songId = Number(newValue.replace('offline-', ''));
        setCurrentOfflineSongId(songId);
        getAudioBlobUrl(songId).then((freshUrl) => {
          if (freshUrl) {
            setOfflineSongUrl(freshUrl);
          }
        });
      } else {
        setCurrentOfflineSongId(null);
        setOfflineSongUrl(newValue ?? null);
      }
    },
    [getAudioBlobUrl, currentOfflineSongId]
  );

  const handleOfflinePlaylistAutoNext = useCallback(() => {
    if (!offlinePlaylistData || currentOfflineSongId === null) {
      return;
    }
    const currentIdx = offlinePlaylistData.songs.findIndex((s) => s.id === currentOfflineSongId);
    const nextIdx = currentIdx + 1;
    if (nextIdx < offlinePlaylistData.songs.length) {
      const nextSong = offlinePlaylistData.songs[nextIdx];
      setShouldAutoPlayOfflineNext(true);
      setCurrentOfflineSongId(nextSong.id);
      getAudioBlobUrl(nextSong.id).then((freshUrl) => {
        if (freshUrl) {
          setOfflineSongUrl(freshUrl);
        }
      });
    }
  }, [offlinePlaylistData, currentOfflineSongId, getAudioBlobUrl]);

  useEffect(() => {
    if (!shouldAutoPlayOfflineNext || !offlineSongUrl) {
      return;
    }

    const audioEl = offlinePlaylistPlayerRef.current?.audio?.current;
    if (!audioEl) {
      return;
    }

    const playPromise = audioEl.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => undefined);
    }

    setShouldAutoPlayOfflineNext(false);
  }, [shouldAutoPlayOfflineNext, offlineSongUrl]);

  //Offline playlist mode (hasEvent=false, playlistId provided)
  if (!isOnline && !hasEvent && playlistId) {
    if (!offlinePlaylistData) {
      return (
        <div className='flex h-full items-center justify-center'>
          <Label
            text={t('playlistNotDownloaded')}
            type={LABEL_TYPE.PARAGRAPH2}
            className='text-secondary-steel-blue'
          />
        </div>
      );
    }
    return (
      <div className='no-scrollbar flex size-full flex-col overflow-auto'>
        <AudioPlayer
          ref={offlinePlaylistPlayerRef}
          customIcons={{ play: <PlayIcon />, pause: <PauseIcon /> }}
          autoPlay={false}
          src={offlineSongUrl ?? undefined}
          className='mb-4 w-full'
          onEnded={handleOfflinePlaylistAutoNext}
        />
        <ChosenSongsContainer
          audioAssets={offlinePlaylistAssets}
          songUrl={currentOfflineSongId ? `offline-${currentOfflineSongId}` : undefined}
          setSongUrl={setOfflineSongUrlBridge}
          deleteSong={() => undefined}
          isTab
          hasEvent={false}
          playlistId={playlistId}
          changePreviewData={() => undefined}
          readOnly
        />
      </div>
    );
  }

  return (
    <>
      {/*Offline mode: ceremony Music tab*/}
      {!isOnline && isTab && hasEvent && (
        <div className='no-scrollbar flex size-full flex-col overflow-auto px-4 py-6 md:px-0'>
          {offlineSongs.length === 0 ? (
            <div className='flex h-40 items-center justify-center text-sm text-secondary-steel-blue'>
              {t('noDownloadedSongs')}
            </div>
          ) : (
            <>
              <AudioPlayer
                customIcons={{ play: <PlayIcon />, pause: <PauseIcon /> }}
                autoPlay={false}
                src={offlineSongUrl ?? undefined}
                className='mb-4 w-full'
              />
              <ChosenSongsContainer
                audioAssets={offlineEventAssets}
                songUrl={offlineSongUrl ?? undefined}
                setSongUrl={setOfflineSongUrlBridge}
                deleteSong={() => undefined}
                isTab
                hasEvent={false}
                eventId={eventId ? Number(eventId) : undefined}
                changePreviewData={() => undefined}
                readOnly
              />
            </>
          )}
        </div>
      )}

      {(isOnline || !isTab || !hasEvent) && (
        <>
          {hasEvent &&
            (isLoadingTags ||
              (hasContentManagementAccess && isLoadingAssets) ||
              isLoadingEvent) && (
              <div className='flex min-h-[60vh] items-center justify-center'>
                <Spinner />
              </div>
            )}
          {(!hasEvent ||
            (!isLoadingTags && hasContentManagementAccess ? !isLoadingAssets : true)) && (
            <div
              className={`flex h-full flex-col ${
                hasOrgAccess || isCreateVideo
                  ? 'w-full'
                  : 'max-w-[960px] md:min-w-full lg:min-w-[960px]'
              } ${hasContentManagementAccess || hasMusicProviderAccess ? '' : 'relative'}`}>
              {!hasOrgAccess && (
                <div className='mb-4 flex flex-row items-center border-b border-secondary-blue-gray pb-[15px]'>
                  <Label text={t('music')} type={LABEL_TYPE.H2} />
                  {!isCreateVideo && (
                    <div className='z-10 inline-flex w-full flex-col items-end gap-2.5 px-2'>
                      <div className='font-sans text-xl font-semibold text-primary-blue'>
                        {`${assetsAudio?.length ?? 0}/${groupedAssetSlots.get(AUDIO)?.length}`}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {hasOrgAccess ? (
                <Label
                  type={LABEL_TYPE.PARAGRAPH2}
                  text={t('pleaseAddMusic')}
                  className='font-sans md:hidden'
                />
              ) : (
                <Label
                  type={LABEL_TYPE.PARAGRAPH2}
                  text={
                    isCreateVideo
                      ? t('pleaseSelectMusic')
                      : t('pleaseSelectSongs', { number: groupedAssetSlots.get(AUDIO)?.length })
                  }
                  className='font-sans md:hidden'
                />
              )}
              <>
                <div
                  className={`${
                    isTab ? 'justify-end' : 'justify-between'
                  } hidden items-center md:order-1 md:flex`}>
                  {!isTab && hasOrgAccess && (
                    <Label text={t('music')} type={LABEL_TYPE.H2} className='self-start' />
                  )}
                  <div
                    className={`flex flex-row items-center ${
                      hasOrgAccess ? 'justify-end' : 'justify-between'
                    } w-full`}>
                    {!hasOrgAccess && (
                      <Label
                        text={t(
                          isCreateVideo
                            ? 'pleaseSelectMusic'
                            : slotsLength === 1
                              ? 'pleaseSelectSong'
                              : 'pleaseSelectSongs',
                          { number: slotsLength }
                        )}
                        type={LABEL_TYPE.PARAGRAPH2}
                        className={'mt-3 flex items-center justify-center self-start'}
                      />
                    )}
                    <div data-testid={TestIds.eventMusic.uploadButton}>
                      <UploadButton
                        ref={uploadButtonRef}
                        multiple
                        text='uploadAudio'
                        pickerTypes={UPLOAD_PICKER_TYPES.AUDIO}
                        isUploading={isUploadingAudio}
                        disabled={
                          hasContentManagementAccess
                            ? !hasOrgAccess && !isCreateVideo
                              ? handleDisable()
                              : false
                            : true
                        }
                        icon={<MusicNoteIcon fill={COLORS.PRIMARY_BLUE} className='mr-1' />}
                        onFilesSelected={uploadAudioFiles}
                      />
                    </div>
                  </div>
                </div>
              </>
              {!isTab && hasOrgAccess && (
                <Label
                  text={t('chooseTheMusicYouWant')}
                  type={LABEL_TYPE.PARAGRAPH1}
                  className='hidden font-semibold md:order-2 md:flex md:!font-semibold'
                />
              )}
              <div
                className={`${
                  isCreateVideo ? 'md:grid' : 'grid'
                } order-1 mt-4 gap-x-6 md:order-3 md:grid-cols-2 md:gap-x-14`}>
                <div className={'w-full'}>
                  {isTab && (
                    <Label
                      text={t('chooseMusicDuringCeremony')}
                      type={LABEL_TYPE.PARAGRAPH1}
                      className='mb-6 hidden font-semibold md:order-2 md:flex md:!font-semibold'
                    />
                  )}
                  <div className={hasOrgAccess ? 'w-full' : 'flex w-full flex-row-reverse gap-2'}>
                    <div data-testid={TestIds.eventMusic.uploadButton}>
                      <UploadButton
                        multiple
                        text='uploadAudio'
                        pickerTypes={UPLOAD_PICKER_TYPES.AUDIO}
                        isUploading={isUploadingAudio}
                        disabled={
                          hasContentManagementAccess
                            ? !hasOrgAccess && !isCreateVideo && handleDisable()
                            : true
                        }
                        icon={<MusicNoteIcon fill={COLORS.PRIMARY_BLUE} className='mr-1' />}
                        showMaxFileSize={false}
                        className={`${hasOrgAccess ? 'w-full' : 'w-1/3'} !px-0 md:hidden`}
                        onFilesSelected={uploadAudioFiles}
                      />
                    </div>
                    <div
                      className={`${hasOrgAccess ? 'mt-4 w-full md:mt-2' : 'w-2/3 md:mt-2 md:w-full'}`}>
                      <Search
                        callback={searchCallback}
                        disabled={!hasMusicProviderAccess}
                        testId={TestIds.eventMusic.searchInput}
                      />
                    </div>
                  </div>
                  {search.length < 2 ? (
                    <div className='mt-4 hidden justify-between gap-2 md:flex'>
                      <Label
                        text={
                          hasMusicProviderAccess
                            ? t('searchForMusicOnline')
                            : t('yourOranizationDoesntHavePermission', {
                                permission: t(`${ORGANIZATION_PERMISSION_TYPE.MUSIC_PROVIDER}`)
                              })
                        }
                        type={LABEL_TYPE.PARAGRAPH1}
                        className='text-secondary-gray'
                      />
                      {showMinLengthMessage && (
                        <Label
                          text={t('nameMin')}
                          type={LABEL_TYPE.PARAGRAPH1}
                          className='!text-accent-red'
                        />
                      )}
                    </div>
                  ) : (
                    <>
                      {search.length > 0 && hasMusicProviderAccess && isLoading ? (
                        <div className='mt-10'>
                          <Spinner />
                        </div>
                      ) : (
                        <div className='mt-4 flex flex-col gap-2'>
                          {(hasMusicProviderAccess ? !isLoading : true) && search.length > 0 && (
                            <p className='text-lg font-bold text-primary-blue'>
                              {t('searchResults')}
                            </p>
                          )}
                          {search.length > 0 &&
                          music?.data.length === 0 &&
                          (hasMusicProviderAccess ? !isLoading : true) ? (
                            <Label
                              text={t('noResults')}
                              type={LABEL_TYPE.PARAGRAPH1}
                              className='text-secondary-gray'
                            />
                          ) : (
                            <div
                              className='overflow-x-none mt-4 flex h-64 flex-col gap-2 overflow-y-scroll pr-4 scrollbar md:h-[26rem]'
                              data-testid={TestIds.eventMusic.searchResults}>
                              {music?.data.map((song: DownloadSong) => (
                                <div
                                  key={song.id}
                                  data-testid={getTestId(
                                    TestIds.eventMusic.searchResultRow,
                                    String(song.id)
                                  )}
                                  className='flex flex-row items-center justify-between gap-2'>
                                  <div
                                    className={`flex flex-row items-center ${
                                      isCreateVideo
                                        ? hasOrgAccess
                                          ? 'w-[70%] md:w-3/5 xl:w-[70%]'
                                          : 'w-3/5 lg:w-[70%]'
                                        : 'w-[70%]'
                                    } `}>
                                    <MusicIcon
                                      fill={
                                        previewSongId === song.id
                                          ? 'var(--color-primary-blue)'
                                          : 'black'
                                      }
                                      className='mr-4 mt-1 size-4 lg:size-6'
                                    />
                                    <p
                                      className={`w-[50vw] truncate ${
                                        previewSongId === song.id &&
                                        'font-semibold text-primary-blue'
                                      }`}>
                                      {song.artist} - {song.title}
                                    </p>
                                  </div>
                                  <div className='flex flex-row items-center gap-4'>
                                    {song.duration && (
                                      <p
                                        className={`${
                                          previewSongId === song.id
                                            ? 'font-semibold text-primary-blue'
                                            : 'text-secondary-gray'
                                        }`}>
                                        {~~(song.duration / 60)}:
                                        {song.duration % 60 < 10
                                          ? `0${song.duration % 60}`
                                          : `${song.duration % 60}`}
                                      </p>
                                    )}
                                    <button
                                      type='button'
                                      data-testid={getTestId(
                                        TestIds.eventMusic.searchResultPreviewButton,
                                        String(song.id)
                                      )}
                                      className='flex items-center justify-center'
                                      onClick={() => {
                                        setPreviewSongId(song.id);
                                        setPreviewSongName(`${song.artist} - ${song.title}`);
                                      }}>
                                      <PlayIcon
                                        className={`hover:text-primary-blue ${
                                          previewSongId === song.id
                                            ? 'text-primary-blue'
                                            : 'text-secondary-gray'
                                        }`}
                                      />
                                    </button>
                                    {(hasOrgAccess ||
                                      (!hasOrgAccess && (isCreateVideo || !handleDisable()))) && (
                                      <button
                                        type='button'
                                        data-testid={getTestId(
                                          TestIds.eventMusic.searchResultAddButton,
                                          String(song.id)
                                        )}
                                        className='flex items-center justify-center'
                                        onClick={() => handleClick(song, true)}>
                                        <PlusIcon className='text-secondary-gray hover:text-primary-blue' />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className='overflow-hidden'>
                  <div data-testid={TestIds.eventMusic.player}>
                    <AudioPlayer
                      customIcons={{ play: <PlayIcon />, pause: <PauseIcon /> }}
                      header={<p className='mt-2 text-sm font-semibold'>{previewSongName}</p>}
                      autoPlay={false}
                      src={songUrl}
                      className={` ${isTab ? 'md:mt-4' : 'md:mt-1'} mt-4 w-full ${
                        !songUrl && 'opacity-50'
                      } ${hasOrgAccess ? 'mt-0 md:mb-8' : 'mb-4'}`}
                    />
                  </div>
                  {((isCreateVideo && !hasOrgAccess) || hasOrgAccess) && (
                    <ChosenSongsContainer
                      audioAssets={
                        hasEvent ? (isTab ? selectedSongsTab : selectedSongs) : playlistAssets
                      }
                      songUrl={songUrl}
                      setSongUrl={setSongUrl}
                      deleteSong={deleteChosenSong}
                      isTab={isTab}
                      uploadingSong={selectedPublicSong}
                      isUploading={isLoadingDownload}
                      isUploaderPage={!isCreateVideo && !hasOrgAccess}
                      changePreviewData={changePreviewData}
                      setAudioAssets={
                        hasEvent
                          ? isTab
                            ? setSelectedSongsTab
                            : (setSelectedSongs as unknown as React.Dispatch<
                                React.SetStateAction<Asset[]>
                              >)
                          : setPlaylistAssets
                      }
                      hasEvent={hasEvent}
                      eventId={eventId ? Number(eventId) : undefined}
                      playlistId={playlistId}
                      hasOfflineMusicDownloadAccess={organizationPermissions.includes(
                        ORGANIZATION_PERMISSION_TYPE.OFFLINE_MUSIC_DOWNLOAD
                      )}
                      onZeroStateIconClick={() => uploadButtonRef.current?.openFileDialog()}
                    />
                  )}
                  {(!hasOrgAccess || isMobile) && hasEvent && (
                    <div
                      data-testid={TestIds.eventMusic.chosenSongs}
                      className={`flex flex-col ${
                        hasOrgAccess && 'mt-4 rounded-xl border border-secondary-blue-gray'
                      } overflow-hidden`}>
                      <div className='flex flex-col'>
                        {hasOrgAccess && (
                          <Label
                            text={t('alreadySelectedSongs')}
                            type={LABEL_TYPE.H4}
                            className='m-4 !font-medium text-primary-blue'
                          />
                        )}
                      </div>
                      <div
                        className={`flex flex-col ${
                          hasOrgAccess
                            ? 'w-full px-4'
                            : 'mb-6 mt-4 rounded-xl border border-secondary-blue-gray p-4 md:mb-14'
                        }`}>
                        {!hasOrgAccess && assets.length !== 0 && hasEvent && (
                          <Label
                            text={t(isCreateVideo ? 'alreadySelectedSongs' : 'chosenSongs')}
                            type={LABEL_TYPE.H4}
                            className='mb-4 text-primary-blue'
                          />
                        )}

                        {assets.map((song: Asset, index) => (
                          <div key={song.id} className='mb-2 flex items-center'>
                            <MusicIcon fill='var(--color-primary-black)' className='h-4 w-6' />
                            <p
                              className={`ml-3 max-w-[60%] cursor-pointer truncate whitespace-nowrap text-sm font-normal ${
                                song?.assetUrl === songUrl && '!font-bold'
                              }`}
                              onClick={() => {
                                setSongUrl(song?.assetUrl);
                              }}>
                              {song.assetName}
                            </p>
                            {isCreateVideo ? (
                              <IconButton
                                variant={IconButtonVariant.SECONDARY}
                                handleOnClick={() => handleClick(song)}
                                className='ml-auto flex items-center justify-center rounded-md bg-primary-gray'>
                                <PlusIcon className='w-3' />
                              </IconButton>
                            ) : (
                              !hasOrgAccess &&
                              currentUser?.id === song?.userId && (
                                <IconButton
                                  variant={IconButtonVariant.SECONDARY}
                                  handleOnClick={() => deleteChosenSong(song.id, index)}
                                  className='ml-auto flex items-center justify-center rounded-md bg-primary-gray'>
                                  {' '}
                                  <DeleteIcon className='w-2.5' />
                                </IconButton>
                              )
                            )}
                          </div>
                        ))}
                        {!isTab && !hasOrgAccess && assets.length === 0 && (
                          <div className='flex flex-col items-center justify-center py-4'>
                            <NoMusicIcon className='text-[22px]' />
                            <Label
                              type={LABEL_TYPE.PARAGRAPH2}
                              text={t('noChosenSongs')}
                              className='pt-[7px] font-sans !text-xs font-medium text-secondary-gray'
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {!isTab && hasOrgAccess && hasEvent && (
                    <div className='rounded-xl md:my-9 md:border md:border-secondary-blue-gray'>
                      <div className='hidden items-center justify-between md:flex'>
                        <Label
                          text={t('alreadySelectedSongs')}
                          type={LABEL_TYPE.H4}
                          className='m-6 !font-normal'
                        />
                        {songTagOptions.length > 0 && (
                          <div className='mr-6'>
                            <MultiSelect
                              className='w-36'
                              label=''
                              id='songTagFilter'
                              placeholder={t('labels')}
                              options={songTagOptions}
                              handleChange={(value) => setSelectedSongTags(value)}
                              selectedValues={selectedSongTags}
                              textColor={COLORS.PRIMARY_BLUE}
                              borderStyle={`1px solid ${COLORS.PRIMARY_BLUE}`}
                              fontWeight='700'
                              dropdownIndicatorColor={COLORS.PRIMARY_BLUE}
                              controlShouldRenderValue={false}
                            />
                          </div>
                        )}
                      </div>
                      {selectedSongTags.length > 0 && (
                        <div className='mx-4 hidden flex-row flex-wrap gap-y-2 md:flex'>
                          {selectedSongTags.map((tag) => (
                            <AssetTagItem
                              tag={tag}
                              handleTagRemove={handleSongTagRemove}
                              key={tag.value as string}
                            />
                          ))}
                        </div>
                      )}
                      <div className='m-4 mt-2 hidden max-h-48 flex-col overflow-y-auto pr-4 scrollbar md:flex'>
                        {filteredAssets?.map((song: Asset) => (
                          <div
                            key={song.id}
                            className='mb-2 flex flex-row items-center justify-between'>
                            <div className='flex flex-row gap-2'>
                              <MusicIcon
                                fill={
                                  previewSongId === song.id ? 'var(--color-primary-blue)' : 'black'
                                }
                                className='mr-2 size-4 md:ml-2'
                              />
                              <Label
                                text={song.assetName}
                                type={LABEL_TYPE.PARAGRAPH2}
                                className={`${
                                  previewSongId === song.id && 'font-semibold text-primary-blue'
                                } truncate ${hasOrgAccess ? 'w-[320px]' : 'w-[200px]'}`}
                              />
                            </div>
                            <div className='flex flex-row items-center gap-4'>
                              {song.duration && (
                                <p
                                  className={`${
                                    previewSongId === song.id
                                      ? 'font-semibold text-primary-blue'
                                      : 'text-secondary-gray'
                                  }`}>
                                  {' '}
                                  {~~(song.duration / 60)}:
                                  {song.duration % 60 < 10
                                    ? `0${song.duration % 60}`
                                    : `${song.duration % 60}`}
                                </p>
                              )}
                              <PlayIcon
                                className={`cursor-pointer hover:text-primary-blue ${
                                  previewSongId === song.id
                                    ? 'text-primary-blue'
                                    : 'text-secondary-gray'
                                }`}
                                onClick={() => {
                                  setSongUrl(song.assetUrl);
                                  setPreviewSongName(song.assetName);
                                }}
                              />
                              <PlusIcon
                                className='cursor-pointer text-secondary-gray hover:text-primary-blue'
                                onClick={() => {
                                  handleClick(song);
                                }}
                              />
                            </div>
                          </div>
                        ))}
                        {isLoadingDownload && !isTab && (
                          <div className='flex items-center py-1'>
                            <MusicIcon fill='var(--color-primary-black)' className='h-4 w-6' />
                            <div className='ml-3 max-w-[80%] cursor-pointer truncate whitespace-nowrap text-sm font-normal'>
                              {selectedPublicSong?.title ?? ''}
                            </div>
                            <div className='ml-auto size-4 overflow-hidden'>
                              <Spinner />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div />
              </div>
              {!(hasContentManagementAccess || hasMusicProviderAccess) && (
                <div className='absolute inset-0 z-10 backdrop-blur-sm'>
                  <Modal
                    isOpen={!(hasContentManagementAccess || hasMusicProviderAccess)}
                    modalClasses='md:w-[450px] w-full border border-secondary-blue-gray'
                    hasCloseIcon={false}
                    onClose={() => {
                      undefined;
                    }}>
                    <div className='flex w-full flex-col items-center justify-center gap-y-4 text-center'>
                      <Label type={LABEL_TYPE.PARAGRAPH1_BOLD} text={t('featureUnavailable')} />
                      <Label
                        type={LABEL_TYPE.PARAGRAPH2}
                        text={t('yourOranizationDoesntHavePermission', {
                          permission:
                            t(`${ORGANIZATION_PERMISSION_TYPE.CONTENT_MANAGEMENT}`) +
                            ', ' +
                            t(`${ORGANIZATION_PERMISSION_TYPE.MUSIC_PROVIDER}`)
                        })}
                      />
                    </div>
                  </Modal>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </>
  );
};
